/**
 * The exercise library read, as `queryOptions`.
 *
 * **Fetch-all, and it is a considered choice rather than a shortcut.** `GET /exercise` takes a
 * `search` and an `ids` filter, and both are deliberately unused here:
 *
 * · The builder needs the muscle attribution of every exercise the OPEN PLAN references, which is
 *   a set that changes as the trainer navigates phases. An `ids`-scoped read would refetch on every
 *   phase tab, and would produce a cache entry per plan rather than one the whole app shares.
 * · The library rail (PR-C) filters the whole catalogue client-side across several facets. A
 *   server-side `search` would reintroduce a request per keystroke, and would make "nothing matched
 *   your filter" indistinguishable from "the catalogue is empty".
 *
 * The catalogue is a few hundred rows of short records — the seeded one is 257 — and it is the same
 * list for every trainer, so one entry serving every surface is both cheaper and simpler than a
 * scoped read per surface.
 *
 * ── Thirty minutes, not `Infinity` ───────────────────────────────────────────────────────────
 * Unlike the plan tree, this is reference data nothing local edits, so a refetch cannot destroy
 * unsaved work — the reason the plan read is frozen does not apply. It changes rarely but it does
 * change: a trainer who adds a custom exercise on their phone should see it here without a reload.
 * Thirty minutes is long enough that navigating the builder never refetches it and short enough
 * that a session started this morning is not looking at yesterday's catalogue.
 *
 * ── `lang` is in the key, and its absence is silent ──────────────────────────────────────────
 * Exercise names are server-localised. A blank `lang` resolves to English rather than erroring, so
 * an omission does not fail — the trainer just reads "Barbell Bench Press" in a Polish interface.
 */

import { queryOptions } from '@tanstack/react-query';

import { getExercise } from '~/shared/api/generated/endpoints/exercise/exercise';
import type { ContentLanguage } from '~/shared/i18n/contentLanguage';

/**
 * The language-blind prefix. Invalidating it drops every language's copy at once, which is what
 * creating or editing an exercise wants: the catalogue changed, so no render of it is still true.
 */
export const exerciseLibraryRoot = ['exercise', 'library'] as const;

export function exerciseLibraryQueryKey(lang: ContentLanguage) {
  return [...exerciseLibraryRoot, lang] as const;
}

export function exerciseLibraryQueryOptions(lang: ContentLanguage) {
  return queryOptions({
    queryKey: exerciseLibraryQueryKey(lang),
    queryFn: ({ signal }) => getExercise({ lang }, undefined, signal),
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * The same catalogue read, scoped to a HANDFUL of ids — a lean batch, exactly as the phone does it
 * (`features/exercises/hooks/useExercisesByIds`).
 *
 * ── Why this exists beside the fetch-all above rather than instead of it ──────────────────────
 * The two answer different questions. The builder and the library rail want the whole catalogue and
 * filter it; the client detail's Ankiety tab wants the NAMES of the two or three exercises an
 * `ExerciseClip` question points at, and it is a screen that never shows a catalogue. Making it
 * read the fetch-all would pull a few hundred rows onto a surface that needs three of them, and
 * would tie an unrelated tab to the thirty-minute entry the builder keeps warm.
 *
 * ── The key is the SET, sorted, not the array ────────────────────────────────────────────────
 * Two callers asking for `[7, 3]` and `[3, 7]` are asking one question, and an unsorted key would
 * make them two cache entries and two requests. Duplicates go the same way — a form asking about
 * one exercise twice is one id.
 *
 * ── `enabled` rather than a request for nothing ──────────────────────────────────────────────
 * `GET /exercise?ids=` with an empty list is the fetch-all under a different name, so a surface
 * with no clip questions asks nothing at all. That costs the caller a flag it must not read
 * naively, and `lib/useExerciseNames` is where that is handled and explained — use it rather than
 * these options plus a hand-written pending predicate.
 */
export function exercisesByIdsQueryOptions(ids: readonly number[], lang: ContentLanguage) {
  const unique = [...new Set(ids)].sort((left, right) => left - right);
  return queryOptions({
    queryKey: [...exerciseLibraryRoot, 'by-ids', lang, unique] as const,
    queryFn: ({ signal }) => getExercise({ ids: unique, lang }, undefined, signal),
    enabled: unique.length > 0,
    staleTime: 30 * 60 * 1000,
  });
}
